import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AdminRoute } from './AdminRoute.js'
import { Login } from './pages/Login.js'
import { TenantList } from './pages/TenantList.js'
import { TenantCreate } from './pages/TenantCreate.js'
import { TenantDetail } from './pages/TenantDetail.js'
import { InvoiceList } from './pages/InvoiceList.js'
import { InvoiceDetail } from './pages/InvoiceDetail.js'
import { PlanTiers } from './pages/PlanTiers.js'
import { AuditLog } from './pages/AuditLog.js'
import { Admins } from './pages/Admins.js'
import { Account } from './pages/Account.js'
import { System } from './pages/System.js'
import { AdminAnalytics } from './pages/AdminAnalytics.js'
import { useApplyAdminTheme } from './hooks/useAdminTheme.js'
import './theme/adminTokens.css'

// One QueryClient for the whole admin tree. Refetch-on-focus is on by
// default in v5; mutations invalidate their own keys explicitly.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: true, retry: 1 },
  },
})

/** Wraps a page in the auth gate — every route except login uses this. */
function gated(element: React.ReactNode) {
  return <AdminRoute>{element}</AdminRoute>
}

/**
 * Admin sub-application. Mounted at /admin/* by the top-level router. Owns
 * the QueryClient, the dark-themed `.admin-root` scope, and toast surface.
 * Login is public; everything else is gated by <AdminRoute>.
 */
export function AdminApp() {
  // Apply the saved admin theme (data-admin-theme on <html>) tree-wide,
  // including the public login route. ThemeToggle in the header owns the
  // user-facing switch; both read/write the same attribute + storage key.
  useApplyAdminTheme()

  return (
    <QueryClientProvider client={queryClient}>
      <div className="admin-root">
        <Routes>
          <Route path="login" element={<Login />} />

          {/* Tenants */}
          <Route path="tenants" element={gated(<TenantList />)} />
          <Route path="tenants/new" element={gated(<TenantCreate />)} />
          <Route path="tenants/:id" element={gated(<TenantDetail />)} />

          {/* Invoices */}
          <Route path="invoices" element={gated(<InvoiceList />)} />
          <Route path="invoices/:id" element={gated(<InvoiceDetail />)} />

          {/* Cross-cutting admin surfaces */}
          <Route path="analytics" element={gated(<AdminAnalytics />)} />
          <Route path="plan-tiers" element={gated(<PlanTiers />)} />
          <Route path="audit-log" element={gated(<AuditLog />)} />
          <Route path="admins" element={gated(<Admins />)} />
          <Route path="account" element={gated(<Account />)} />
          <Route path="system" element={gated(<System />)} />

          <Route index element={<Navigate to="/admin/tenants" replace />} />
          <Route path="*" element={<Navigate to="/admin/tenants" replace />} />
        </Routes>
      </div>
      <Toaster theme="dark" position="bottom-right" richColors />
    </QueryClientProvider>
  )
}
